/**
 * Input Focus Helper Utilities
 * 
 * Utilities to handle input focus in mobile bottom sheets,
 * ensuring inputs are visible when keyboard appears.
 */

/**
 * Production-grade scroll into view for mobile inputs
 * Mimics native app behavior with immediate + delayed scroll
 */
export function scrollInputIntoView(
  element: HTMLElement | null,
  options: {
    delay?: number
    block?: ScrollLogicalPosition
    behavior?: ScrollBehavior
    immediate?: boolean
  } = {}
) {
  if (!element) return
  
  const {
    delay = 150, // Reduced from 300ms for faster response
    block = 'center', // Center for better visibility
    behavior = 'smooth',
    immediate = true // Scroll immediately, then again after delay
  } = options

  const scrollOptions = {
    behavior,
    block,
    inline: 'nearest' as ScrollLogicalPosition
  }

  // Immediate scroll (prevents input being hidden)
  if (immediate) {
    element.scrollIntoView({
      ...scrollOptions,
      behavior: 'auto' // Instant for immediate scroll
    })
  }

  // Delayed scroll (after keyboard animation completes)
  setTimeout(() => {
    element.scrollIntoView(scrollOptions)
  }, delay)
}

/**
 * Production-grade input focus handler
 * Handles focus, click, and touch events for comprehensive coverage
 */
export function addInputFocusHandler(
  input: HTMLInputElement | HTMLTextAreaElement | null,
  options?: {
    delay?: number
    block?: ScrollLogicalPosition
    immediate?: boolean
  }
) {
  if (!input) return () => {}
  
  const scrollTimeout: ReturnType<typeof setTimeout> | null = null
  
  const handleFocus = () => {
    // Clear any pending scroll
    if (scrollTimeout) clearTimeout(scrollTimeout)
    
    // Scroll input into view
    scrollInputIntoView(input, {
      ...options,
      immediate: true // Always immediate on focus
    })
  }
  
  // Handle both focus and click for better coverage
  input.addEventListener('focus', handleFocus)
  input.addEventListener('click', handleFocus)
  
  // Handle touch events for mobile
  const handleTouchStart = () => {
    // Don't prevent default - let browser handle it
    // Just ensure scroll happens
    handleFocus()
  }
  
  input.addEventListener('touchstart', handleTouchStart, { passive: true })
  
  return () => {
    if (scrollTimeout) clearTimeout(scrollTimeout)
    input.removeEventListener('focus', handleFocus)
    input.removeEventListener('click', handleFocus)
    input.removeEventListener('touchstart', handleTouchStart)
  }
}

/**
 * Sets inputmode attribute on input for better mobile keyboard
 */
export function setInputMode(
  input: HTMLInputElement | HTMLTextAreaElement | null,
  mode: 'text' | 'email' | 'tel' | 'url' | 'numeric' | 'decimal' | 'search' = 'text'
) {
  if (!input) return
  input.setAttribute('inputmode', mode)
}
